import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges, OnInit } from '@angular/core';

import { EventModel, SetTab } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore, SitecoreEventDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';

import { EpcotNotificationComponent } from '../../../common/epcotNotifications/notification.component';
import { BetBuilderTrackingValues } from '../bet-builder-tracking';
import { BetBuilderEventNameService } from '../services/bet-builder-event-name.service';
import { BetBuilderHowToDialogService } from '../services/bet-builder-how-to-dialog.service';
import { BetBuilderOnboardingMessageService } from './bet-builder-onboarding-message.service';

@Component({
    selector: 'ms-bet-builder-onboarding',
    templateUrl: 'bet-builder-onboarding-message.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [EpcotNotificationComponent, NgIf],
})
export class BetBuilderOnboardingComponent implements OnInit, OnChanges {
    @Input() event: EventModel;
    @Input() setId: string | number;

    @HostBinding('class') className = 'bet-builder-onboarding-message';

    messageText: string;
    linkText: string;

    private dismissed = false;

    constructor(
        sitecore: Sitecore,
        private betBuilderHowToDialogService: BetBuilderHowToDialogService,
        private betBuilderEventNameService: BetBuilderEventNameService,
        private onboardingMessageService: BetBuilderOnboardingMessageService,
        private tracker: TrackingService,
        private sitecoreEventDetails: SitecoreEventDetailsConfig,
    ) {
        this.messageText = sitecore.betBuilder.messages?.EventDetailsOnboardingMessageText ?? '';
        this.linkText = sitecore.betBuilder.messages?.EventDetailsOnboardingLinkText ?? '';
    }

    onCloseButtonClick(): void {
        this.dismissed = true;
        this.onboardingMessageService.close();
        this.trackCloseButtonClick();
    }

    onLinkClick(): void {
        this.trackLinkClick();

        const leagueId = this.event.league.id;
        const sportId = this.event.sport.id;
        const eventName = this.betBuilderEventNameService.getParticipantHeaderTitle({
            participants: this.event.participants,
            leagueId,
            sportId,
            eventViewType: this.event.viewType,
            eventName: this.event.name,
        });

        this.betBuilderHowToDialogService.openBuilder({ leagueId, sportId, eventName });
    }

    ngOnInit(): void {
        this.onboardingMessageService.incrementCounter();
        this.trackLoad();
    }

    ngOnChanges(changes: ISimpleChanges<BetBuilderOnboardingComponent>): void {
        this.tryReplaceMessagesText();

        if (changes.setId?.previousValue === SetTab.BetBuilder) {
            this.onboardingMessageService.tryMarkAsShown();
        }
    }

    get shouldShow(): boolean {
        return !this.dismissed && !this.onboardingMessageService.isCounterFinished();
    }

    private trackLinkClick(): void {
        this.tracker.track(trackingConstants.EVENT_TRACKING, {
            ...this.getTrackingObject(),
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetBuilderTrackingValues.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: BetBuilderTrackingValues.InfoRibbon,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: BetBuilderTrackingValues.FindOutMore,
        });
    }

    private trackLoad(): void {
        if (!this.shouldShow) {
            return;
        }

        this.tracker.track(trackingConstants.EVENT_CONTENT_VIEW, {
            ...this.getTrackingObject(),
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetBuilderTrackingValues.Load,
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: BetBuilderTrackingValues.InfoRibbon,
        });
    }

    private trackCloseButtonClick(): void {
        this.tracker.track(trackingConstants.EVENT_TRACKING, {
            ...this.getTrackingObject(),
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetBuilderTrackingValues.Close,
            [trackingConstants.COMPONENT_POSITION_EVENT]: BetBuilderTrackingValues.InfoRibbon,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: BetBuilderTrackingValues.InfoRibbon,
        });
    }

    private getTrackingObject = () => {
        return {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetBuilderTrackingValues.BetBuilder,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetBuilderTrackingValues.BuildaBetPod,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.event.name,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        };
    };

    private tryReplaceMessagesText(): void {
        const sportName = SportConstant[this.event.sport.id].toString();
        let messageText: string | undefined;

        if (this.event.betBuilderInfo?.betBuilderId && this.setId !== SetTab.BetBuilder) {
            messageText = this.sitecoreEventDetails.onboarding[`OnboardingTextLeague_${this.event.league.id}`];
            this.linkText = this.sitecoreEventDetails.onboarding['OnboardingLinkText'];

            if (!messageText) {
                messageText = this.sitecoreEventDetails.onboarding[`OnboardingText${sportName}`];
            }
        }

        if (messageText) {
            this.messageText = messageText;
        }
    }
}
